import Phaser from 'phaser';
import { Z } from './zOrder';
import { regions } from './layout';
import { ColorPickerView } from './ColorPickerView';
import { makeTwoPassText, type TwoPassText } from './twoPassText';
import type { PuppyGame } from '../game/PuppyGame';
import type { PuppyId, Color } from '../game/types';

// Renders the top region: sky+clouds backdrop, active-puppy panel (top-left),
// FIDO doghouse + bowl (center), paw menu (top-right), CrazySquare tile +
// count badge (right), and score text along the horizon.
export class HeaderView {
  readonly container: Phaser.GameObjects.Container;
  private sky: Phaser.GameObjects.Image;
  private clouds: Phaser.GameObjects.Image[];
  private scoreText: TwoPassText;
  private colorPicker: ColorPickerView;
  private ground: Phaser.GameObjects.Image;
  readonly dogHouse: Phaser.GameObjects.Image;
  private dogHouseFull: Phaser.GameObjects.Image;
  private dogHouseSign: Phaser.GameObjects.Image;
  private eyes: Phaser.GameObjects.Image[];
  private dogBowl: Phaser.GameObjects.Image;
  readonly undoButton: Phaser.GameObjects.Image;
  private undoBounceTween: Phaser.Tweens.Tween | null = null;
  readonly crazyTile: Phaser.GameObjects.Image;
  private crazyBadge: Phaser.GameObjects.Image;
  private crazyCountText: TwoPassText;
  private useCrazySign: Phaser.GameObjects.Image;
  readonly pawMenuButton: Phaser.GameObjects.Image;

  constructor(
    scene: Phaser.Scene,
    private game: PuppyGame,
    private onBowlTap?: () => void,
  ) {
    const r = regions().header;
    this.container = scene.add.container(0, 0).setDepth(Z.menu);

    // Sky fills the whole header and stays put. The ground/trees image is
    // shifted ~4 grid squares (60px) down from the top so more sky is
    // visible at the top of the header (with clouds drifting through it),
    // and the trees appear lower on the horizon.
    this.sky = scene.add
      .image(r.x, r.y, 'sky6plus')
      .setOrigin(0, 0)
      .setDisplaySize(r.width, r.height)
      .setDepth(Z.sky);
    const groundOffset = 60;
    this.ground = scene.add
      .image(r.x, r.y + groundOffset, 'ground6plus')
      .setOrigin(0, 0)
      .setDisplaySize(r.width, r.height)
      .setDepth(Z.grassAndHills);
    // Synonym used by cloud / horizon positioning below.
    const skyH = Math.round(r.height * 0.25);
    const groundH = r.height - skyH;
    void groundH; // keep the binding for grassMidY math below

    // Three clouds drifting horizontally near the top of the sky band.
    // The ground/trees image starts lower (groundOffset) so most clouds
    // sit clearly in the open sky; the lowest one (cloud2) dips a bit and
    // gets occluded by the tree line when it drifts past. Each cloud starts
    // offscreen-left and drifts in — the y positions vary so they don't all
    // share a horizon line. animateClouds applies different speeds + stagger.
    const cloudLeftWrap = r.x - 80;
    this.clouds = [
      scene.add.image(cloudLeftWrap, r.y + 30, 'cloud1'),
      scene.add.image(cloudLeftWrap, r.y + 55, 'cloud2'),
      scene.add.image(cloudLeftWrap, r.y + 18, 'cloud3'),
    ];
    for (const c of this.clouds) {
      c.setOrigin(0.5).setScale(0.9).setDepth(Z.clouds);
    }
    this.animateClouds(scene, r);

    // Active-puppy panel: 15px from left and top edges (one debug-grid square
    // in from each). Width and height bumped 3 grid squares (45px) so the
    // outer frame spans (15..156) instead of (15..111). ColorPickerView frame
    // is sizePx + 16 wide/tall, so sizePx of 125 produces a 141×141 panel.
    // 3 grid squares (45 px) wider/taller than the previous 125-px panel.
    const panelSize = 170;
    const panelHalf = (panelSize + 16) / 2;
    this.colorPicker = new ColorPickerView(
      scene,
      r.x + 15 + panelHalf,
      r.y + 15 + panelHalf,
      panelSize,
    );

    // Doghouse + bowl sit on the ground band, slightly above the horizon
    // line so they read as standing on the grass.
    const grassMidY = r.y + skyH + groundH * 0.55;
    const dhX = r.x + 330;
    // Preserve doghouse2 native aspect (199×137). Stretching to a square
    // made it look "tall" and squished the FIDO label vertically.
    const dhW = 216;
    const dhH = Math.round(dhW * (137 / 199));
    // Lower bound chosen so the doghouse bottom edge sits ON the shadow
    // ellipse's centre line, anchoring it visually.
    const dhBottomY = grassMidY + 23;
    const dhY = dhBottomY - dhH / 2;
    // Soft drop shadow underneath the doghouse — a flat elongated ellipse
    // that grounds the sprite on the grass.
    const dhShadow = scene.add
      .ellipse(dhX + 4, dhBottomY + 4 - 18, dhW * 0.85, 24, 0x000000, 0.45)
      .setDepth(Z.dogHouse - 1)
      .setOrigin(0.5);
    this.dogHouse = scene.add
      .image(dhX, dhY, 'doghouse2')
      .setDisplaySize(dhW, dhH)
      .setDepth(Z.dogHouse)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    // Eye sprites peek through the doghouse window as dogs accumulate
    // (dogsInDoghouse). Positions/textures ported from Header.swift L1597-1614,
    // scaled to match the 140×140 doghouse (iOS used 87px square).
    const eyeScale = dhW / 87;
    const eyeW = 22 * eyeScale * 0.5;
    const eyeH = 16 * eyeScale * 0.5;
    // Per-eye nudges in screen pixels (applied after the eyeScale-based
    // base offset). 1 grid square = 15 px.
    const eyeOffsets: Array<{ x: number; y: number; nudgeX: number; nudgeY: number; frame: string }> = [
      { x: -31, y: 3,  nudgeX: 15,  nudgeY: 6.25,  frame: 'eyes2@2x.png' }, // +1 right, +0.42 down (was 0.75, raised 1/3)
      { x:  14, y: 4,  nudgeX: 7.5, nudgeY: 0,     frame: 'eyes1@2x.png' }, // +0.5 right
      { x:  19, y: 16, nudgeX: 0,   nudgeY: 0,     frame: 'eyes3@2x.png' },
    ];
    this.eyes = eyeOffsets.map(({ x: ex, y: ey, nudgeX, nudgeY, frame }) =>
      scene.add
        .image(dhX + ex * eyeScale + nudgeX, dhY + ey * eyeScale + nudgeY, 'eyes', frame)
        .setDisplaySize(eyeW, eyeH)
        .setDepth(Z.eyes)
        .setOrigin(0.5)
        .setVisible(false),
    );

    // Mirrors iOS Header.animateDogHouseEyes — every 1s, each VISIBLE eye
    // randomly swaps to one of the 3 eye textures. Reads as the pups inside
    // glancing around.
    const EYE_FRAMES = ['eyes1@2x.png', 'eyes2@2x.png', 'eyes3@2x.png'];
    scene.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => {
        for (const eye of this.eyes) {
          if (!eye.visible) continue;
          const f = EYE_FRAMES[Math.floor(Math.random() * EYE_FRAMES.length)]!;
          eye.setTexture('eyes', f);
        }
      },
    });

    // "Dog house full!" sign that fades in when all 3 dogs are inside.
    // Anchored 1 debug-grid square (15px) below the base position.
    this.dogHouseFull = scene.add
      .image(dhX, dhY + dhW * 0.42 + 15, 'doghousefull')
      .setDisplaySize(dhW * 0.95, dhW * 0.2)
      .setDepth(Z.dogHouseFull)
      .setOrigin(0.5)
      .setVisible(false);

    // "Go to the Dog House!" prompt arrow — appears above the doghouse when
    // the active puppy has no playable squares. Slight tilt mirrors iOS.
    // Anchored 180px below and 150px left of the doghouse-relative base
    // (12 down / 10 left in debug-grid squares of 15px).
    this.dogHouseSign = scene.add
      .image(dhX - 150, dhY - dhW * 0.55 + 180, 'doghousesign2')
      .setDisplaySize(220, 95)
      .setDepth(Z.goToDogHouse)
      .setOrigin(0.5)
      .setRotation(-Math.PI / 24)
      .setVisible(false);

    // Dogbowl: smaller, preserves native aspect (50×42 ≈ 1.19:1). Left edge
    // tucked even closer to the doghouse — slight overlap so the bowl
    // reads as right alongside it. Tappable: rocks back and forth a few
    // beats then settles.
    const bowlW = 50;
    const bowlH = Math.round(bowlW * (42 / 50));
    const bowlLeftX = dhX + dhW / 2 - 10;
    this.dogBowl = scene.add
      .image(bowlLeftX + bowlW / 2, dhBottomY - bowlH / 2, 'dogbowl2')
      .setDisplaySize(bowlW, bowlH)
      .setDepth(Z.dogBowl)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    this.dogBowl.on('pointerdown', () => this.rockBowl());

    // "Undo!" pill button — visible for a few seconds after each of the
    // first three placements. Centred over the Big Puppy panel so it sits
    // on top of the active-puppy preview during the training-wheels window.
    const panelCx = r.x + 15 + panelHalf;
    const panelCy = r.y + 15 + panelHalf;
    this.undoButton = scene.add
      .image(panelCx, panelCy, 'undo')
      .setOrigin(0.5)
      .setDisplaySize(149 * 0.8, 69 * 0.8)
      .setDepth(Z.undoCantUndo)
      .setInteractive({ useHandCursor: true })
      .setVisible(false);

    // Paw menu: flush against the top edge of the screen, preserving the
    // native 83×63 aspect ratio (was previously stretched to 90×90).
    const pawW = 90;
    const pawH = Math.round(pawW * (63 / 83));
    this.pawMenuButton = scene.add
      .image(r.x + r.width - 15, r.y, 'menupaw')
      .setDisplaySize(pawW, pawH)
      .setDepth(Z.menu)
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });

    // CrazySquare tile + count badge (below paw menu, right side). Same
    // treatment as the board MegaPuppy formation: red crazysquareframe just
    // inside the tile area, with the crazysquare image rotating continuously
    // inside the frame. Frame visible at FRAME_OUTER × tile, bg+mask at
    // INNER × tile, bg oversized BG_OVERSCAN × so rotation at any angle
    // still fills the masked area (BG_OVERSCAN ≥ √2).
    const tileBox = 90;
    const TILE_FRAME_OUTER = 0.97;
    const TILE_INNER = 0.93;
    const TILE_BG_OVERSCAN = 1.45;
    const tileCx = r.x + r.width - 70;
    const tileCy = r.y + 130;
    const tileBorderSize = tileBox * TILE_FRAME_OUTER * (73 / 68);
    const crazyBorder = scene.add
      .image(tileCx, tileCy, 'crazysquareframe')
      .setOrigin(34 / 73, 34 / 73)
      .setDisplaySize(tileBorderSize, tileBorderSize)
      .setCrop(0, 0, 68, 68)
      .setDepth(Z.colorSquare);
    this.crazyTile = scene.add
      .image(tileCx, tileCy, 'crazysquare2')
      .setDisplaySize(tileBox * TILE_INNER * TILE_BG_OVERSCAN, tileBox * TILE_INNER * TILE_BG_OVERSCAN)
      .setDepth(Z.colorPickerFrame)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    const tileMaskSize = tileBox * TILE_INNER;
    const tileMaskG = scene.make.graphics({ x: 0, y: 0 });
    tileMaskG.fillStyle(0xffffff, 1);
    tileMaskG.fillRect(tileCx - tileMaskSize / 2, tileCy - tileMaskSize / 2, tileMaskSize, tileMaskSize);
    tileMaskG.setVisible(false);
    this.crazyTile.setMask(tileMaskG.createGeometryMask());
    scene.tweens.add({
      targets: this.crazyTile,
      rotation: Math.PI * 2,
      duration: 4000,
      ease: 'Linear',
      repeat: -1,
    });
    // Free-puppy count badge — always visible (shows "0" when none banked).
    // Pinned to the upper-right of the crazysquare tile.
    const badgeX = tileCx + (tileBox / 2) - 5;
    const badgeY = tileCy - (tileBox / 2) + 5;
    this.crazyBadge = scene.add
      .image(badgeX, badgeY, 'crazycountcircle')
      .setDisplaySize(40, 40)
      .setDepth(Z.colorPickerCountCircle)
      .setOrigin(0.5);
    this.crazyCountText = makeTwoPassText(scene, badgeX, badgeY, '0', {
      fontSize: '22px',
      depth: Z.colorPickerCount,
    });

    // "Use a Crazy Square!" prompt — shown only when the player has at
    // least one CrazySquare banked. Hangs just below the crazy tile,
    // right-aligned to the tile's right edge so it doesn't clip the
    // viewport. Native art is 184x100; display at ~half size to match the
    // doghouse sign visual weight.
    this.useCrazySign = scene.add
      .image(tileCx + tileBox / 2 - 60, tileCy + tileBox / 2 + 8 - 150, 'usecrazysquare')
      .setDisplaySize(180, 95)
      .setOrigin(1, 0)
      .setDepth(Z.goToDogHouse)
      .setVisible(false);

    // Score along the horizon, right side.
    this.scoreText = makeTwoPassText(
      scene,
      r.x + r.width - 24,
      r.y + r.height - 9,
      '0',
      { fontSize: '48px', originX: 1, originY: 1, depth: Z.scoreAndLevel },
    );

    // sky → clouds → ground so the trees in the ground image occlude the
    // clouds where they overlap (clouds drift "behind" the foliage). All UI
    // elements render on top of the scenery.
    this.container.add([
      this.sky, ...this.clouds, this.ground,
      this.colorPicker.container,
      dhShadow,
      this.dogHouse, ...this.eyes, this.dogHouseFull, this.dogHouseSign,
      this.dogBowl,
      this.pawMenuButton,
      crazyBorder, this.crazyTile, this.crazyBadge,
      this.crazyCountText.outline, this.crazyCountText.fill,
      this.useCrazySign,
      this.scoreText.outline, this.scoreText.fill,
      this.undoButton,
    ]);
  }

  // Tap-to-rock for the dog bowl. Tilts back-and-forth a few beats then
  // settles upright. No-op when a rock is already in flight so rapid taps
  // don't compound.
  private rockBowl(): void {
    if (this.dogBowl.scene.tweens.isTweening(this.dogBowl)) return;
    this.onBowlTap?.();
    this.dogBowl.scene.tweens.chain({
      targets: this.dogBowl,
      tweens: [
        { angle: 18,  duration: 110, ease: 'Sine.Out' },
        { angle: -14, duration: 130, ease: 'Sine.InOut' },
        { angle: 9,   duration: 130, ease: 'Sine.InOut' },
        { angle: -5,  duration: 130, ease: 'Sine.InOut' },
        { angle: 0,   duration: 160, ease: 'Sine.InOut' },
      ],
    });
  }

  showUndoButton(): void {
    this.undoButton.setVisible(true).setAlpha(0).setScale(0.8);
    this.undoBounceTween?.stop();
    this.undoButton.scene.tweens.add({
      targets: this.undoButton,
      alpha: 1,
      scale: 1,
      duration: 200,
      ease: 'Back.Out',
      onComplete: () => {
        // Continuous yoyo bounce while the button is visible.
        this.undoBounceTween = this.undoButton.scene.tweens.add({
          targets: this.undoButton,
          scale: 1.15,
          duration: 220,
          ease: 'Sine.InOut',
          yoyo: true,
          repeat: -1,
        });
      },
    });
  }

  hideUndoButton(): void {
    this.undoBounceTween?.stop();
    this.undoBounceTween = null;
    this.undoButton.setScale(1);
    this.undoButton.setVisible(false);
  }

  showDogHouseSign(onShown?: () => void): void {
    if (this.dogHouseSign.visible) return;
    // Never show the "Go to the Dog House!" prompt once the doghouse is
    // full — at that point the doghouse can't accept more retreats and the
    // "Dog house full!" sign is the relevant cue.
    if (this.game.dogsInDoghouse >= 3) return;
    this.dogHouseSign.setVisible(true).setAlpha(0).setScale(0.6);
    this.dogHouseSign.scene.tweens.add({
      targets: this.dogHouseSign,
      alpha: 1,
      scale: 1,
      duration: 250,
      ease: 'Back.Out',
    });
    onShown?.();
  }

  hideDogHouseSign(): void {
    if (!this.dogHouseSign.visible) return;
    this.dogHouseSign.setVisible(false);
  }

  setUseCrazySignVisible(visible: boolean): void {
    this.useCrazySign.setVisible(visible);
  }

  // Drift each cloud horizontally across the sky band, wrapping when it
  // leaves the right edge. Mirrors Header.swift::animateClouds. Each cloud
  // is staggered so they enter from offscreen-left at different times rather
  // than all appearing at once.
  private animateClouds(scene: Phaser.Scene, r: { x: number; width: number }): void {
    const speeds = [22, 14, 18]; // px/sec — slight variation per cloud
    const startDelays = [0, 9000, 18000]; // ms — first cloud enters immediately, others later
    const leftWrap = r.x - 80;
    const rightWrap = r.x + r.width + 80;
    for (let i = 0; i < this.clouds.length; i++) {
      const c = this.clouds[i]!;
      const speed = speeds[i] ?? 16;
      const drift = (rightWrap - leftWrap) / speed * 1000;
      c.setX(leftWrap);
      scene.tweens.add({
        targets: c,
        x: rightWrap,
        duration: drift,
        delay: startDelays[i] ?? 0,
        ease: 'Linear',
        repeat: -1,
        onRepeat: () => c.setX(leftWrap),
      });
    }
  }

  refresh(activePuppy?: string, activeColor?: string): void {
    this.scoreText.setText(this.game.gameScore.toLocaleString('en-US'));
    if (activePuppy && activeColor) {
      this.colorPicker.setActive(activePuppy as PuppyId, activeColor as Color);
    }
    const cs = this.game.crazySquares ?? 0;
    // Always show the free-puppies count (0 when none banked).
    this.crazyCountText.setText(String(cs));
    // Visibility of the "Use a Crazy Square!" prompt is driven by MainScene
    // via setUseCrazySignVisible — it should only appear when the active puppy
    // is unplayable AND a CrazySquare is banked. Refresh just clears it if
    // the count drops to 0.
    if (cs === 0) this.useCrazySign.setVisible(false);

    // Reveal eye pairs in the doghouse window as dogs accumulate; show the
    // "doghouse full" sign once all three are inside.
    const dogs = this.game.dogsInDoghouse;
    for (let i = 0; i < this.eyes.length; i++) {
      this.eyes[i]!.setVisible(dogs > i);
    }
    this.dogHouseFull.setVisible(dogs >= 3);
  }
}
